import { create } from "zustand";

export interface Workspace { id: string; name: string; slug: string;
                              organization?: string;
                              organization_slug?: string;
                              organization_name?: string;
                              plan?: string;
                              membership_role?: "owner" | "admin" | "editor" | "viewer" | null;
                              is_member?: boolean;
                              entitlements?: string[];
                              accent_color?: string;
                              logo_url?: string }
export interface User {
  id: number; username: string; email: string;
  is_staff?: boolean; is_superuser?: boolean;
}

export type Theme = "dark" | "light";

interface AppState {
  // Auth
  token: string | null;
  user: User | null;
  setAuth: (token: string, user: User) => void;
  logout: () => void;

  // Workspaces
  workspaceId: string | null;
  workspaces: Workspace[];
  setWorkspace: (id: string | null) => void;
  setWorkspaces: (ws: Workspace[]) => void;
  // Theme
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;

  // Sidebar (responsive)
  sidebarOpen: boolean;       // mobile drawer
  sidebarCollapsed: boolean;  // desktop collapse to icons
  setSidebarOpen: (v: boolean) => void;
  toggleSidebar: () => void;
  toggleSidebarCollapsed: () => void;
}

const KEY_TOKEN     = "ckos.token";
const KEY_USER      = "ckos.user";
const KEY_WS        = "ckos.workspaceId";
const KEY_THEME     = "ckos.theme";
const KEY_TENANT    = "ckos.tenantSchema";
const KEY_COLLAPSED = "ckos.sidebar.collapsed";

function readTheme(): Theme {
  const v = localStorage.getItem(KEY_THEME);
  if (v === "dark" || v === "light") return v;
  if (window.matchMedia?.("(prefers-color-scheme: light)").matches) return "light";
  return "dark";
}

function applyTheme(t: Theme) {
  document.documentElement.dataset.theme = t;
}

export const useApp = create<AppState>((set, get) => {
  const initTheme = readTheme();
  applyTheme(initTheme);

  let user: User | null = null;
  try {
    const raw = localStorage.getItem(KEY_USER);
    user = raw ? (JSON.parse(raw) as User) : null;
  } catch { user = null; }

  return {
    token: localStorage.getItem(KEY_TOKEN),
    user,
    setAuth: (token, user) => {
      localStorage.setItem(KEY_TOKEN, token);
      localStorage.setItem(KEY_USER, JSON.stringify(user));
      set({ token, user });
    },
    logout: () => {
      localStorage.removeItem(KEY_TOKEN);
      localStorage.removeItem(KEY_TENANT);
      localStorage.removeItem(KEY_USER);
      set({ token: null, user: null });
    },

    workspaceId: localStorage.getItem(KEY_WS),
    workspaces: [],
    setWorkspace: (id) => {
      if (id) localStorage.setItem(KEY_WS, id);
      else localStorage.removeItem(KEY_WS);
      // Sync the X-Tenant-Schema header value for the API client.
      const ws = get().workspaces.find((w) => w.id === id);
      const slug = ws?.organization_slug ?? null;
      if (slug) localStorage.setItem(KEY_TENANT, slug);
      else localStorage.removeItem(KEY_TENANT);
      set({ workspaceId: id });
    },
    setWorkspaces: (workspaces) => {
      // Keep the current workspace when it still exists; otherwise
      // fall back to the first available workspace after reload/login.
      const currentId = get().workspaceId;
      const active = workspaces.find((w) => w.id === currentId && w.is_member !== false)
        ?? workspaces.find((w) => w.is_member !== false) ?? null;
      if (active?.organization_slug) localStorage.setItem(KEY_TENANT, active.organization_slug);
      else localStorage.removeItem(KEY_TENANT);
      if (active?.id) localStorage.setItem(KEY_WS, active.id);
      else localStorage.removeItem(KEY_WS);
      set({ workspaces, workspaceId: active?.id ?? null });
    },

    theme: initTheme,
    setTheme: (t) => { localStorage.setItem(KEY_THEME, t); applyTheme(t); set({ theme: t }); },
    toggleTheme: () => {
      const next: Theme = get().theme === "dark" ? "light" : "dark";
      localStorage.setItem(KEY_THEME, next); applyTheme(next); set({ theme: next });
    },

    sidebarOpen: false,
    sidebarCollapsed: localStorage.getItem(KEY_COLLAPSED) === "1",
    setSidebarOpen: (v) => set({ sidebarOpen: v }),
    toggleSidebar: () => set({ sidebarOpen: !get().sidebarOpen }),
    toggleSidebarCollapsed: () => {
      const next = !get().sidebarCollapsed;
      localStorage.setItem(KEY_COLLAPSED, next ? "1" : "0");
      set({ sidebarCollapsed: next });
    },
  };
});
